import type { ErrorEvent } from '@sentry/react';

/** Message substrings we intentionally drop from Sentry (product noise, extensions, third-party). */
export const SENTRY_NOISE_MESSAGE_PATTERNS = [
  'Free plan videos are limited to 5 seconds',
  'Pick a shorter length',
  'shorter length, or upgrade',
  'Rate limit exceeded',
  // Chrome / Chromium wording for DOMException NOT_FOUND_ERR during React teardown
  "Failed to execute 'removeChild' on 'Node'",
  "Failed to execute 'insertBefore' on 'Node'",
  // Safari / WebKit wording for the same class of React DOM races (PIXLIE-REACT-G/F)
  'The object can not be found here',
  'The node before which the new node is to be inserted is not a child of this node',
  'Object Not Found Matching Id:',
  'AbortError: signal is aborted',
  'Extension context invalidated',
  // Cloudflare Web Analytics beacon (injected via CF dashboard) — not app code
  'this.i.at is not a function',
  'beacon.min.js',
] as const;

/** Script URLs whose errors should never pollute pixlie-react. */
export const SENTRY_DENY_URLS: Array<string | RegExp> = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /cloudflareinsights\.com\/beacon\.min\.js/i,
  /static\.cloudflareinsights\.com/i,
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
];

export function isSentryNoiseMessage(message: string): boolean {
  return SENTRY_NOISE_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern));
}

function stackFrames(event: ErrorEvent) {
  return (
    event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? []
  );
}

function exceptionBlob(event: ErrorEvent): string {
  const values = event.exception?.values ?? [];
  const parts = [
    event.message,
    ...values.map((v) => v.type),
    ...values.map((v) => v.value),
    ...stackFrames(event).flatMap((frame) => [
      frame.function,
      frame.filename,
      frame.module,
    ]),
  ];
  return parts.filter(Boolean).join('\n');
}

/**
 * React 19 + Mobile Safari (and some extensions) race DOM teardown:
 * commitDeletionEffectsOnFiber → native removeChild → NotFoundError / DOMException code 8.
 * Chrome message differs; match by stack shape so we do not open a new Sentry issue per browser.
 */
export function isReactRemoveChildDomNoise(event: ErrorEvent): boolean {
  const blob = exceptionBlob(event);
  const looksLikeNotFound =
    /NotFoundError/i.test(blob) ||
    /The object can not be found here/i.test(blob) ||
    /Failed to execute 'removeChild'/i.test(blob) ||
    /DOMException/i.test(blob);

  if (!looksLikeNotFound) return false;

  return (
    /removeChild/i.test(blob) ||
    /insertBefore/i.test(blob) ||
    /commitDeletionEffectsOnFiber/i.test(blob) ||
    /recursivelyTraverseMutationEffects/i.test(blob) ||
    /commitMutationEffectsOnFiber/i.test(blob)
  );
}

/** Drop when every frame (or the top frame) is a known third-party analytics script. */
export function isThirdPartyScriptError(event: ErrorEvent): boolean {
  const frames = stackFrames(event).filter((frame) => frame.filename);
  if (frames.length === 0) return false;

  const thirdPartyPattern =
    /cloudflareinsights\.com|beacon\.min\.js|googletagmanager\.com|google-analytics\.com/i;

  const top = frames[frames.length - 1];
  if (top?.filename && thirdPartyPattern.test(top.filename)) {
    return true;
  }

  return frames.every((frame) => frame.filename && thirdPartyPattern.test(frame.filename));
}

export function shouldDropSentryEvent(event: ErrorEvent): boolean {
  const message =
    event.message ||
    event.exception?.values?.[0]?.value ||
    event.exception?.values?.[0]?.type ||
    '';

  if (isSentryNoiseMessage(String(message))) {
    return true;
  }

  if (isReactRemoveChildDomNoise(event)) {
    return true;
  }

  if (isThirdPartyScriptError(event)) {
    return true;
  }

  return false;
}
